import type { PlanSet } from '../../types/planSets';
import type { PlanSetInput, PurchasedPlan } from '../../interfaces/planSet.interface';
import { PLAN_SET, PLAN_SETS } from '../../common/constants/planSets';
import { isRoundReadyPlanSet } from './roundAvailability';

const PLAN_SET_VALUES: readonly string[] = Object.values(PLAN_SET);

export function parsePlanSet(value: unknown): PlanSet | undefined {
  const raw = Array.isArray(value) ? value[0] : value;

  return typeof raw === 'string' && PLAN_SET_VALUES.includes(raw) ? raw as PlanSet : undefined;
}

function isPlanSetAllowed(planSet: PlanSet, isRound: boolean): boolean {
  return !isRound || isRoundReadyPlanSet(PLAN_SETS[planSet]);
}

function resolvePurchasedPlanSet(purchasedPlan: PurchasedPlan, isRound: boolean): PlanSet | undefined {
  const planSet = parsePlanSet(purchasedPlan.planSet);
  const planId = Array.isArray(purchasedPlan.planId) ? purchasedPlan.planId[0] : purchasedPlan.planId;

  if (!planSet || !isPlanSetAllowed(planSet, isRound)) return undefined;

  return PLAN_SETS[planSet].some(plan => String(plan.id) === String(planId)) ? planSet : undefined;
}

export function resolvePlanSet(input: PlanSetInput): PlanSet {
  if (input.funnelPlanSet) return input.funnelPlanSet;

  const purchasedPlanSet = input.purchasedPlan && resolvePurchasedPlanSet(input.purchasedPlan, input.isRound);

  if (purchasedPlanSet) return purchasedPlanSet;

  const basePlanSet = input.isUSPricing ? PLAN_SET.VARIANT_C : PLAN_SET.BASELINE;
  const upsellPlanSet = input.isInUpsellExperiment() && isPlanSetAllowed(PLAN_SET.UPSELL_111, input.isRound)
    ? PLAN_SET.UPSELL_111
    : basePlanSet;

  return input.pricingExperiments.reduce<PlanSet>((chosen, experiment) => {
    if (!isPlanSetAllowed(experiment.planSet, input.isRound)) return chosen;

    return input.readVariation(experiment.key) === experiment.activeVariant ? experiment.planSet : chosen;
  }, upsellPlanSet);
}
